import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Map as MapIcon, AlertTriangle, Flame, Crown } from "lucide-react";
import { useApi, specLabel } from "@/lib/api";
import { useMe } from "@/context/Me";
import { PageTitle, Empty } from "@/components/bits";
import { JobCard } from "@/pages/Jobs";
import JobMap from "@/components/JobMap";
import { Slider } from "@/components/ui/slider";

export default function Board() {
  const api = useApi();
  const { me, meta } = useMe();
  const [spec, setSpec] = useState("");
  const [radius, setRadius] = useState(50);
  const [data, setData] = useState({ jobs: [], center: null });
  useEffect(() => {
    const t = setTimeout(() => api.get("/jobs/board", { params: { specialty: spec || undefined, radius } }).then((r) => setData(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [api, spec, radius]);
  if (me?.profile_type !== "pro") return <div className="p-10 text-center text-slate-400">O mural é exclusivo para profissionais.</div>;
  const status = me.verification?.status;
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <PageTitle eyebrow={[me.district, me.city].filter(Boolean).join(" · ")} title="Mural de chamados">
        <div className="flex gap-2">
          {!me.is_pro && <Link to="/planos" data-testid="board-go-pro" className="btn-ghost border-purple-500/40 px-4 py-2 text-sm text-purple-200"><Crown size={16} />Seja Pro</Link>}
          {!me.is_featured && me.country === "BR" && <Link to="/planos" data-testid="board-go-featured" className="btn-hazard bg-[#FF5722] px-4 py-2 text-sm text-white hover:bg-[#E04818]"><Flame size={16} />Destaque</Link>}
        </div>
      </PageTitle>
      {status !== "approved" && (
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="board-verification-banner">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          {status === "rejected" ? <span>Sua verificação foi recusada. <Link to="/perfil" className="font-bold underline">Envie novas fotos</Link>.</span> : <span>Sua verificação está em análise. Você já pode ver o mural; as propostas são liberadas após a aprovação.</span>}
        </div>
      )}
      <div className="mb-5 grid gap-4 md:grid-cols-[1fr_280px]">
        <div className="flex flex-wrap gap-2">
          <button data-testid="board-spec-all" onClick={() => setSpec("")} className={`rounded-full px-4 py-2 text-sm font-semibold ${!spec ? "bg-[#FFC700] text-[#111317]" : "border border-white/15 text-slate-300"}`}>Todas</button>
          {(meta?.specialties || []).map((s) => (
            <button key={s} data-testid={`board-spec-${s}`} onClick={() => setSpec(s)} className={`rounded-full px-4 py-2 text-sm font-semibold ${spec === s ? "bg-[#FFC700] text-[#111317]" : "border border-white/15 text-slate-300"}`}>{specLabel(s, me.country)}</button>
          ))}
        </div>
        <div className="panel p-4"><div className="mb-3 flex justify-between text-sm"><span className="text-slate-400">Raio</span><b data-testid="board-radius-value">{radius} km</b></div>
          <Slider data-testid="board-radius" min={5} max={300} step={5} value={[radius]} onValueChange={(v) => setRadius(v[0])} /></div>
      </div>
      <div className="grid gap-5 lg:grid-cols-[1.1fr_1fr]">
        <div className="lg:sticky lg:top-24 lg:self-start"><JobMap jobs={data.jobs} center={data.center} height={460} /></div>
        <div className="space-y-3">
          <div className="text-sm text-slate-400" data-testid="board-count">{data.jobs.length} chamado(s) abertos</div>
          {!data.jobs.length && <Empty icon={MapIcon} title="Nenhum chamado no raio" text="Aumente o raio ou mude a especialidade." />}
          {data.jobs.map((j) => <JobCard key={j.id} j={j} extra={j.already_proposed && <span className="mt-2 inline-block rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-bold text-emerald-300">Proposta enviada</span>} />)}
        </div>
      </div>
    </div>
  );
}
